"use client";

import { useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import type { ParagraphImage } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import styles from "../Paragraph.module.css";

/**
 * ImageGallery — siatka miniatur otwierających `yet-another-react-lightbox`.
 *
 * **Warianty:**
 *   - `grid` (default) — siatka auto-fill z minmax(160px, 1fr), aspect 4:3 na kafel.
 *     Dla layoutow z galeria (4/5/7/13/24).
 *   - `single` — jedno duze zdjecie w aspect-ratio 16:9 (layout 15 big photo).
 *   - `stack` — pionowa kolumna, kazde zdjecie 100% szerokosci kontenera,
 *     wysokosc auto wg aspectu oryginalu. Dla layoutow 2/3 (Tekst + zdjecia
 *     po prawej/lewej) - zdjecia ulozone jedno pod drugim w wlasnej kolumnie
 *     obok tekstu.
 *
 * **Lightbox source:** używamy `image.full.url` (pełen obraz bez
 * miniaturyzacji) — kafel pokazuje thumbnail (`image.url`), lightbox pokazuje
 * full quality.
 *
 * **A11y:** kafle to `<button>` (nie `<a href>` bo nie nawigujemy nigdzie —
 * tylko otwieramy modal). Aria-label z opisem lub fallback "Otwórz zdjęcie".
 */
export function ImageGallery({
  images,
  t,
  variant = "grid",
}: {
  images: ParagraphImage[];
  t: ParagraphTranslations;
  /** `grid` = siatka (domyślnie); `single` = jedno duże zdjęcie (layout 15); `stack` = pionowa kolumna 100% szerokosci (layouty 2/3). */
  variant?: "grid" | "single" | "stack" | "wide-grid";
}) {
  const [openAt, setOpenAt] = useState<number | null>(null);

  if (!images || images.length === 0) {
    return <div className={styles.empty}>{t.gallery.empty}</div>;
  }

  const slides = images.map((img) => ({
    src: img.full?.url || img.url,
    width: img.full?.width ?? img.width ?? undefined,
    height: img.full?.height ?? img.height ?? undefined,
    alt: img.alt ?? img.description ?? "",
    description: img.description ?? undefined,
  }));

  const containerClass =
    variant === "stack"
      ? styles.galleryStack
      : variant === "wide-grid"
        ? styles.galleryWide
        : styles.gallery;
  const itemModifier =
    variant === "single"
      ? styles.galleryItemSingle
      : variant === "stack"
        ? styles.galleryItemStack
        : "";

  return (
    <>
      <div className={containerClass}>
        {images.map((img, i) => (
          <button
            key={img.id}
            type="button"
            className={`${styles.galleryItem} ${itemModifier}`}
            onClick={() => setOpenAt(i)}
            aria-label={img.alt ?? img.description ?? t.gallery.openImage}
          >
            <img
              src={img.url}
              alt={img.alt ?? img.description ?? ""}
              loading="lazy"
              decoding="async"
              width={img.width ?? undefined}
              height={img.height ?? undefined}
            />
            {img.description && (
              <span className={styles.galleryItemDescription}>
                {img.description}
              </span>
            )}
          </button>
        ))}
      </div>

      <Lightbox
        open={openAt !== null}
        index={openAt ?? 0}
        close={() => setOpenAt(null)}
        slides={slides}
      />
    </>
  );
}
